import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  AppCheck,
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
} from 'firebase/app-check';
import { Auth } from 'firebase/auth';
import { Firestore } from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { FirebaseStorage } from 'firebase/storage';
import { BehaviorSubject } from 'rxjs';

import {
  IProjectConfig,
  ProjectLocation,
  projects,
} from '@hawkindynamics/core-client';

import { globalFirebaseConfig } from '../../firebase.config';
import { IFirebaseAppConfig } from '../types';

export interface IAppServices {
  firestore: Firestore;
  auth: Auth;
  functions: Functions;
  storage: FirebaseStorage;
  getGCSImage: (filename: string) => Promise<Blob>;
  app: FirebaseApp;
}

/**
 * Stored tenant configuration for auth persistence
 */
interface StoredTenantConfig {
  appConfig: IFirebaseAppConfig;
  tenantId: string | null;
  email: string;
  timestamp: number;
}

const TENANT_CONFIG_KEY = 'hawkin_tenant_config';

/**
 * Save tenant configuration to localStorage for auth persistence
 */
export const saveTenantConfig = (
  appConfig: IFirebaseAppConfig,
  tenantId: string | null,
  email: string
): void => {
  try {
    const config: StoredTenantConfig = {
      appConfig,
      tenantId,
      email,
      timestamp: Date.now(),
    };
    localStorage.setItem(TENANT_CONFIG_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save tenant config:', e);
  }
};

/**
 * Clear stored tenant configuration (on logout)
 */
export const clearTenantConfig = (): void => {
  try {
    localStorage.removeItem(TENANT_CONFIG_KEY);
  } catch (e) {
    console.error('Failed to clear tenant config:', e);
  }
};

/**
 * Get stored tenant configuration
 */
const getStoredTenantConfig = (): StoredTenantConfig | null => {
  try {
    const stored = localStorage.getItem(TENANT_CONFIG_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as StoredTenantConfig;
  } catch (e) {
    console.error('Failed to parse stored tenant config:', e);
    clearTenantConfig();
    return null;
  }
};

/**
 * Initialize the global Firebase app for identity service only
 * This app is ONLY used for:
 * 1. Providing App Check tokens for /locate endpoint
 * 2. Never for user authentication
 *
 * Users authenticate against the tenant app (tenantApp$) which is
 * dynamically initialized after /locate returns the tenant config
 */
export const globalApp = initializeApp(globalFirebaseConfig, 'global');

/**
 * Tenant app - The ONLY app users authenticate against
 * Starts as null and is set after /locate returns tenant configuration
 * Subscribe to this to react to tenant app initialization
 */
export const tenantApp$ = new BehaviorSubject<FirebaseApp | null>(null);

/**
 * Sets the current tenant app - called from getAuthForTenant
 * This triggers reactive subscriptions in userProvider
 */
export const setTenantApp = (app: FirebaseApp): void => {
  tenantApp$.next(app);
};

/**
 * Initialize App Check for the global app
 * This provides security for identity service requests
 */
let globalAppCheck: AppCheck | null = null;

try {
  // Get the global project config based on environment
  // Check both build-time ENVIRONMENT and runtime hostname
  const isDev =
    ENVIRONMENT !== 'production' ||
    (typeof window !== 'undefined' &&
      window.location.hostname === 'cloud.dev.hawkindynamics.com');
  const globalProjectKey = isDev ? 'global-dev' : 'global';
  const globalProject = projects.find(
    (proj: IProjectConfig): boolean => proj.serviceAccountKey === globalProjectKey
  );

  if (!globalProject) {
    throw new Error(`Global project config not found for key: ${globalProjectKey}`);
  }

  // Enable App Check debug mode for CI preview deployments
  // The debug token must be registered in Firebase Console
  if (FIREBASE_APPCHECK_DEBUG_TOKEN) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      FIREBASE_APPCHECK_DEBUG_TOKEN;
  }

  globalAppCheck = initializeAppCheck(globalApp, {
    provider: new ReCaptchaEnterpriseProvider(globalProject.recaptchaEnterpriseKey),
    isTokenAutoRefreshEnabled: true,
  });
} catch (e) {
  console.warn('App Check initialization failed:', e);
}

export { globalAppCheck };

/**
 * Tenant App Check instances - stored by app name
 * Used for App Check tokens in tenant-specific operations (e.g., SAML bridge)
 */
const tenantAppCheckInstances = new Map<string, AppCheck>();

/**
 * Get the tenant App Check instance for the current tenant app
 */
export const getTenantAppCheck = (): AppCheck | null => {
  const app = tenantApp$.value;
  if (!app) return null;
  return tenantAppCheckInstances.get(app.name) || null;
};

/**
 * Sets the tenant App Check instance
 * Called from getAuthForTenant when AppCheck is initialized
 */
export const setTenantAppCheck = (appName: string, appCheck: AppCheck): void => {
  tenantAppCheckInstances.set(appName, appCheck);
};

/**
 * Gets the configuration for a Firebase app by project ID
 * Used for functions, storage, etc. after tenant-specific app is initialized
 */
export const getRegionConfig = (
  app: FirebaseApp
): {
  projectLocation: ProjectLocation;
  userImageBucket: string;
  siteKey: string;
  domain: string;
} => {
  // Try to find by app name first (for legacy apps), then by projectId
  let project = projects.find(
    (proj: IProjectConfig): boolean => proj.serviceAccountKey === app.name
  );

  if (!project) {
    // For dynamically created apps, look up by the id field which matches Firebase projectId
    const projectId = app.options.projectId;
    project = projects.find(
      (proj: IProjectConfig): boolean => proj.id === projectId
    );
  }

  if (!project) {
    throw new Error(`No project configuration found for app: ${app.name} (projectId: ${app.options.projectId})`);
  }

  return {
    projectLocation: project.functionsLocation,
    userImageBucket: project.userImageBucket,
    siteKey: project.recaptchaEnterpriseKey,
    domain: project.functionsDomain,
  };
};

/**
 * Get stored tenant configuration (exported for auth.ts to use on startup)
 */
export { getStoredTenantConfig };

